import React from 'react';
import { withStyles, WithStyles } from '@mui/styles';
import type { Theme } from '@mui/material/styles';
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  ContactMail as ContactIcon,
  Image as ImageIcon,
  Web as WebIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import VendorImageUpload from './VendorImageUpload';
import VendorContactManager from './VendorContactManager';
import VendorServiceAreaManager from './VendorServiceAreaManager';
import DataService from '../../services/data-service';

const styles = (theme: Theme) => ({
  root: {
    padding: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2),
    },
  },
  responsiveHeader: {
    fontSize: '1.1rem',
    [theme.breakpoints.up('md')]: {
      fontSize: '1.25rem',
    },
  },
  section: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(3),
    },
  },
  sectionHeader: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  submitButton: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  accordion: {
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
  textField: {
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
  multilineField: {
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
});

interface VendorAddress {
  street?: string;
  street2?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  countryCode?: string;
}

interface VendorImageData {
  fileNameOrURLPath: string;
  label?: string;
  width: number;
  height: number;
}

interface VendorData {
  recordID: string;
  companyName?: string;
  companyDescription?: string;
  contactName?: string;
  businessPhone?: string;
  businessEmail?: string;
  websiteUrl?: string;
  address?: VendorAddress;
  socialMediaUrls?: string[];
  additionalContacts?: unknown[];
  serviceAreas?: unknown[];
  listingLevel?: string;
  photos?: VendorImageData[];
  logoImage?: VendorImageData | null;
  profileImage?: VendorImageData | null;
  listingBackgroundImage?: VendorImageData | null;
  meta?: { updatedAt?: Date };
}

interface ImageUpdateResult {
  success: boolean;
  action?: string;
  imageUrl?: string;
  imageType?: string;
  message?: string;
  fileName?: string;
  dimensions?: { width: number; height: number };
}

// Structurally compatible with VendorContactManager's ExternalContact
type VendorContact =
  | { phone: { number: string; label: string } }
  | { email: { email: string; label: string } };

// Structurally compatible with VendorServiceAreaManager's ExternalServiceArea
interface VendorServiceArea {
  name?: string;
  geoLocation: { latitude: number; longitude: number };
  radiusMeters: number;
}

interface FormData {
  companyName: string;
  companyDescription: string;
  contactName: string;
  businessPhone: string;
  businessEmail: string;
  websiteUrl: string;
  address: {
    street: string;
    street2: string;
    city: string;
    region: string;
    postalCode: string;
    countryCode: string;
  };
  socialMediaUrls: string[];
  additionalContacts: VendorContact[];
  serviceAreas: VendorServiceArea[];
  listingLevel: string;
  meta?: { updatedAt?: Date };
}

interface ExpandedSections {
  basic: boolean;
  contact: boolean;
  images: boolean;
  social: boolean;
  service: boolean;
}

interface VendorEditFormOwnProps {
  vendor: VendorData | null;
  onVendorUpdate?: (vendor: VendorData) => void;
}

interface VendorEditFormProps extends VendorEditFormOwnProps, WithStyles<typeof styles> {}

interface VendorEditFormState {
  formData: FormData;
  loading: boolean;
  error: string | null;
  success: boolean;
  expandedSections: ExpandedSections;
  showBrandedDialog: boolean;
}

class VendorEditForm extends React.Component<VendorEditFormProps, VendorEditFormState> {
  constructor(props: VendorEditFormProps) {
    super(props);

    this.state = {
      formData: this.initializeFormData(props.vendor),
      loading: false,
      error: null,
      success: false,
      expandedSections: {
        basic: true,
        contact: false,
        images: false,
        social: false,
        service: false,
      },
      showBrandedDialog: false,
    };
  }

  initializeFormData = (vendor: VendorData | null): FormData => ({
    companyName: vendor?.companyName ?? '',
    companyDescription: vendor?.companyDescription ?? '',
    contactName: vendor?.contactName ?? '',
    businessPhone: vendor?.businessPhone ?? '',
    businessEmail: vendor?.businessEmail ?? '',
    websiteUrl: vendor?.websiteUrl ?? '',
    address: {
      street: vendor?.address?.street ?? '',
      street2: vendor?.address?.street2 ?? '',
      city: vendor?.address?.city ?? '',
      region: vendor?.address?.region ?? '',
      postalCode: vendor?.address?.postalCode ?? '',
      // countryCode is required on the wire; default new/legacy records to USA.
      countryCode: vendor?.address?.countryCode ?? 'USA',
    },
    socialMediaUrls: vendor?.socialMediaUrls ?? [],
    additionalContacts: (vendor?.additionalContacts as VendorContact[]) ?? [],
    serviceAreas: (vendor?.serviceAreas as VendorServiceArea[]) ?? [],
    listingLevel: vendor?.listingLevel ?? '',
  });

  override componentDidUpdate(prevProps: VendorEditFormProps): void {
    if (prevProps.vendor !== this.props.vendor) {
      this.setState({
        formData: this.initializeFormData(this.props.vendor),
      });
    }
  }

  handleInputChange =
    (field: string) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      this.setState((prevState) => ({
        formData: {
          ...prevState.formData,
          [field]: value,
        },
      }));
    };

  handleAddressChange =
    (field: string) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      this.setState((prevState) => ({
        formData: {
          ...prevState.formData,
          address: {
            ...prevState.formData.address,
            [field]: value,
          },
        },
      }));
    };

  handleSocialMediaChange = (urls: string[]): void => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        socialMediaUrls: urls,
      },
    }));
  };

  handleContactsChange = (contacts: VendorContact[]): void => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        additionalContacts: contacts,
      },
    }));
  };

  handleServiceAreasChange = (serviceAreas: VendorServiceArea[]): void => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        serviceAreas,
      },
    }));
  };

  handleAccordionChange =
    (panel: keyof ExpandedSections) =>
    (_event: React.SyntheticEvent, isExpanded: boolean): void => {
      this.setState((prevState) => ({
        expandedSections: {
          ...prevState.expandedSections,
          [panel]: isExpanded,
        },
      }));
    };

  handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    this.setState({ loading: true, error: null, success: false });

    try {
      const dataService = new DataService();
      const { vendor } = this.props;
      const { formData } = this.state;

      const updateData = formData;
      if (updateData.meta) {
        updateData.meta.updatedAt = new Date();
      }

      const updatedVendor = (await dataService.updateVendor(
        vendor!.recordID,
        updateData,
      )) as VendorData;

      console.log(updatedVendor);

      this.setState({
        loading: false,
        success: true,
        error: null,
      });

      if (this.props.onVendorUpdate) {
        this.props.onVendorUpdate(updatedVendor);
      }

      setTimeout(() => {
        this.setState({ success: false });
      }, 3000);
    } catch (error) {
      console.error('Error updating vendor:', error);
      const message = error instanceof Error ? error.message : String(error);
      this.setState({
        loading: false,
        error: `Failed to update vendor: ${message}`,
      });
    }
  };

  handleImageUpdate = (imageResult: unknown): void => {
    const result = imageResult as ImageUpdateResult;
    console.log(result);
    if (!result.success) {
      this.setState({
        loading: false,
        error: `Failed to process image: ${result.message ?? ''}`,
      });
      return;
    }

    switch (result.action) {
      case 'delete':
        this.handleImageDeleted(result.imageUrl, result.imageType);
        break;
      case 'upload': {
        const { vendor } = this.props;
        if (vendor && vendor.listingLevel !== 'branded') {
          this.setState({ showBrandedDialog: true });
        }

        const newImage: VendorImageData = {
          fileNameOrURLPath: result.imageUrl ?? '',
          ...(result.fileName !== undefined ? { label: result.fileName } : {}),
          width: result.dimensions?.width ?? 0,
          height: result.dimensions?.height ?? 0,
        };
        this.handleImageUploaded(newImage, result.imageType);
        break;
      }
    }
  };

  handleImageDeleted = (imageUrl?: string, imageType?: string): void => {
    const { vendor } = this.props;
    if (!vendor) return;

    switch (imageType) {
      case 'photos': {
        if (!vendor.photos) return;
        const index = vendor.photos.findIndex(
          (imageInfo) => imageInfo.fileNameOrURLPath === imageUrl,
        );
        if (index > -1) {
          vendor.photos.splice(index, 1);
        }
        break;
      }
      case 'listingBackgroundImage':
        vendor.listingBackgroundImage = null;
        break;
      case 'logoImage':
        vendor.logoImage = null;
        break;
      case 'profileImage':
        vendor.profileImage = null;
        break;
      default:
        console.log('Unknown vegetable');
    }

    if (this.props.onVendorUpdate) {
      this.props.onVendorUpdate(vendor);
    }
  };

  handleImageUploaded = (imageInfo: VendorImageData, imageType?: string): void => {
    const { vendor } = this.props;
    if (!vendor) return;

    switch (imageType) {
      case 'photos':
        if (!vendor.photos) {
          vendor.photos = [];
        }
        vendor.photos.push(imageInfo);
        break;
      case 'listingBackgroundImage':
        vendor.listingBackgroundImage = imageInfo;
        break;
      case 'logoImage':
        vendor.logoImage = imageInfo;
        break;
      case 'profileImage':
        vendor.profileImage = imageInfo;
        break;
      default:
        console.log('Unknown fruit');
    }

    if (this.props.onVendorUpdate) {
      this.props.onVendorUpdate(vendor);
    }
  };

  handleBrandedToggleChange = async (event: {
    target: { checked: boolean };
  }): Promise<void> => {
    const { vendor } = this.props;
    const isChecked = event.target.checked;
    const newListingLevel = isChecked ? 'branded' : '';

    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        listingLevel: newListingLevel,
      },
    }));

    try {
      const dataService = new DataService();
      const updateData = { listingLevel: newListingLevel };

      const updatedVendor = (await dataService.updateVendor(
        vendor!.recordID,
        updateData,
      )) as VendorData;

      console.log('Branded level updated:', updatedVendor);

      if (this.props.onVendorUpdate) {
        this.props.onVendorUpdate(updatedVendor);
      }
    } catch (error) {
      console.error('Error updating branded level:', error);
      const message = error instanceof Error ? error.message : String(error);
      this.setState({
        error: `Failed to update branded level: ${message}`,
      });

      this.setState((prevState) => ({
        formData: {
          ...prevState.formData,
          listingLevel: this.props.vendor?.listingLevel ?? '',
        },
      }));
    }
  };

  handleBrandedDialogClose = (confirm: boolean): void => {
    if (confirm) {
      this.setState(
        (prevState) => ({
          formData: {
            ...prevState.formData,
            listingLevel: 'branded',
          },
        }),
        () => {
          void this.handleBrandedToggleChange({ target: { checked: true } });
        },
      );
    }

    this.setState({ showBrandedDialog: false });
  };

  override render(): React.JSX.Element {
    const { classes, vendor } = this.props;
    const { formData, loading, error, success, expandedSections, showBrandedDialog } =
      this.state;

    if (!vendor) {
      return <Alert severity="warning">No vendor data available for editing.</Alert>;
    }

    return (
      <form onSubmit={this.handleSubmit} className={classes.root}>
        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ marginBottom: 2 }}>
            Vendor information updated successfully!
          </Alert>
        )}

        <Box sx={{ marginBottom: 2, padding: 2, border: '1px solid #ccc', borderRadius: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.listingLevel === 'branded'}
                onChange={this.handleBrandedToggleChange}
                color="primary"
              />
            }
            label="Branded Enabled"
          />
          <Typography variant="body2" color="textSecondary" sx={{ marginLeft: 4 }}>
            Toggle to enable Branded StashCard features and listing enhancements.
          </Typography>
        </Box>

        <Accordion
          expanded={expandedSections.basic}
          onChange={this.handleAccordionChange('basic')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="h6"
              className={`${classes.sectionHeader} ${classes.responsiveHeader}`}
            >
              <BusinessIcon sx={{ fontSize: 'inherit' }} />
              Basic Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.companyName}
                  onChange={this.handleInputChange('companyName')}
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Description"
                  value={formData.companyDescription}
                  onChange={this.handleInputChange('companyDescription')}
                  className={classes.multilineField}
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Name"
                  value={formData.contactName}
                  onChange={this.handleInputChange('contactName')}
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website URL"
                  value={formData.websiteUrl}
                  onChange={this.handleInputChange('websiteUrl')}
                  className={classes.textField}
                  type="url"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedSections.contact}
          onChange={this.handleAccordionChange('contact')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="h6"
              className={`${classes.sectionHeader} ${classes.responsiveHeader}`}
            >
              <ContactIcon sx={{ fontSize: 'inherit' }} />
              Contact Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Business Phone"
                  value={formData.businessPhone}
                  onChange={this.handleInputChange('businessPhone')}
                  className={classes.textField}
                  placeholder="(999) 999-9999"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Business Email"
                  value={formData.businessEmail}
                  onChange={this.handleInputChange('businessEmail')}
                  className={classes.textField}
                  type="email"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom className={classes.responsiveHeader}>
                  <LocationIcon
                    sx={{ marginRight: 1, verticalAlign: 'middle', fontSize: 'inherit' }}
                  />
                  Address
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.address.street}
                  onChange={this.handleAddressChange('street')}
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address 2 (Optional)"
                  value={formData.address.street2}
                  onChange={this.handleAddressChange('street2')}
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.address.city}
                  onChange={this.handleAddressChange('city')}
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State / Region"
                  value={formData.address.region}
                  onChange={this.handleAddressChange('region')}
                  className={classes.textField}
                  placeholder="CA"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.address.postalCode}
                  onChange={this.handleAddressChange('postalCode')}
                  className={classes.textField}
                />
              </Grid>

              <Grid item xs={12}>
                <VendorContactManager
                  contacts={formData.additionalContacts}
                  onChange={this.handleContactsChange}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedSections.images}
          onChange={this.handleAccordionChange('images')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="h6"
              className={`${classes.sectionHeader} ${classes.responsiveHeader}`}
            >
              <ImageIcon sx={{ fontSize: 'inherit' }} />
              Images
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <VendorImageUpload
              vendor={vendor as unknown as React.ComponentProps<typeof VendorImageUpload>['vendor']}
              onImageUpdate={this.handleImageUpdate}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedSections.social}
          onChange={this.handleAccordionChange('social')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="h6"
              className={`${classes.sectionHeader} ${classes.responsiveHeader}`}
            >
              <WebIcon sx={{ fontSize: 'inherit' }} />
              Social Media URLs
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SocialMediaManager
              urls={formData.socialMediaUrls}
              onChange={this.handleSocialMediaChange}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedSections.service}
          onChange={this.handleAccordionChange('service')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="h6"
              className={`${classes.sectionHeader} ${classes.responsiveHeader}`}
            >
              <LocationIcon sx={{ fontSize: 'inherit' }} />
              Service Areas
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <VendorServiceAreaManager
              serviceAreas={formData.serviceAreas}
              onChange={this.handleServiceAreasChange}
            />
          </AccordionDetails>
        </Accordion>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          className={classes.submitButton}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? 'Updating...' : 'Update Vendor'}
        </Button>

        <Dialog open={showBrandedDialog} onClose={() => this.handleBrandedDialogClose(false)}>
          <DialogTitle>Elevate to Branded StashCard Vendor</DialogTitle>
          <DialogContent>
            <Typography>
              This listing is not Branded, would you like to elevate it to a Branded StashCard
              Vendor?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.handleBrandedDialogClose(false)}>No</Button>
            <Button
              onClick={() => this.handleBrandedDialogClose(true)}
              variant="contained"
              color="primary"
              autoFocus
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </form>
    );
  }
}

interface SocialMediaManagerProps {
  urls: string[];
  onChange: (urls: string[]) => void;
}

class SocialMediaManager extends React.Component<SocialMediaManagerProps> {
  handleUrlChange = (index: number, value: string): void => {
    const newUrls = [...this.props.urls];
    newUrls[index] = value;
    this.props.onChange(newUrls);
  };

  handleAddUrl = (): void => {
    this.props.onChange([...this.props.urls, '']);
  };

  handleRemoveUrl = (index: number): void => {
    const newUrls = this.props.urls.filter((_, i) => i !== index);
    this.props.onChange(newUrls);
  };

  override render(): React.JSX.Element {
    const { urls } = this.props;

    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Social Media URLs
        </Typography>
        {urls.map((url, index) => (
          <Box key={index} display="flex" alignItems="center" gap={1} mb={2}>
            <TextField
              fullWidth
              label={`Social Media URL ${index + 1}`}
              value={url}
              onChange={(e) => this.handleUrlChange(index, e.target.value)}
              type="url"
              placeholder="https://facebook.com/yourpage"
            />
            <Button
              color="error"
              onClick={() => this.handleRemoveUrl(index)}
              variant="outlined"
              size="small"
            >
              Remove
            </Button>
          </Box>
        ))}
        <Button variant="outlined" color="primary" onClick={this.handleAddUrl} size="small">
          Add Social Media URL
        </Button>
      </Box>
    );
  }
}

export default withStyles(styles)(VendorEditForm);
